// QR Kiosk v2 — KV2 message-level page-artifact card (schema / diagram / cross-
// reference). The bot attaches schema pages as STRUCTURED artifacts on
// `message.artifacts[]` (`source: "cross_reference"` / `type: "diagram_overlay"`),
// NOT as inline <list-ref> tags. KioskMidasFullV2.artifactMap already maps both to
// `page → {url,title}`; this file renders those same artifacts as tappable end-of-
// message cards and opens the EXISTING MidasArtifactViewV2 via the surface context.
// Mirrors the desktop V3 `sources` cards (ChatBotMessageV3) adapted to kiosk tokens.
// Extracted from MidasMessages.tsx to keep that file < 300 lines. Tokens + glyphs
// only — MUI-free, no fontStyle:italic. §8.4.
import React, { useContext } from "react";
import { ChatMessage } from "../../../shared/chatBot/models/chatBotModels";
import { Glyph, GD, Spark } from "../shared/glyphs";
import { usePressed } from "../shared/usePressed";
import { MidasSurfaceContext } from "./midasSurfaceContext";
import { useBotImage } from "./useBotImage";
import { resolveKioskArtifactImageUrl, resolveKioskArtifactPage } from "./kioskArtifactImage";

export type KioskPageCard = { key: string; page: number | null; url: string; title: string; label: string };

// Extract page-image artifacts for a message, reusing the SAME per-artifact
// resolution KioskMidasFullV2.artifactMap uses (single source of truth =
// resolveKioskArtifactImageUrl). Schema/diagram pages resolve to the
// `/v1/bot/diagram/{page}/image` endpoint (desktop parity), NOT `data.image_url`.
export const pageArtifacts = (msg: ChatMessage): KioskPageCard[] => {
  const out: KioskPageCard[] = [];
  for (const a of msg.artifacts || []) {
    if (a.source !== "cross_reference" && a.type !== "diagram_overlay") continue;
    const url = resolveKioskArtifactImageUrl(a);
    if (!url) continue;
    const d = (a.data || {}) as any;
    const page = resolveKioskArtifactPage(a);
    const listId = typeof d.list_id === "string" ? d.list_id : undefined;
    const label = listId ? `List ${listId}` : page != null ? `Page ${page}` : "Schéma";
    out.push({ key: `${a.source}-${a.type}-${page ?? url}`, page, url, title: a.title || d.name || "", label });
  }
  return out;
};

// Tappable card: mono page/list label + title + schematic thumbnail. Tap opens the
// existing artifact viewer through the surface (page → openPageArtifact resolves via
// artifactMap; non-numeric page → openImageArtifact fallback).
export function PageArtifactCard({ card }: { card: KioskPageCard }) {
  const surface = useContext(MidasSurfaceContext);
  const blob = useBotImage(card.url);
  const press = usePressed();
  const open = () => {
    if (card.page != null) surface?.openPageArtifact(card.page);
    else surface?.openImageArtifact(card.url, card.title);
  };
  return (
    <button
      type="button"
      {...press.pressHandlers}
      onClick={open}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 11,
        width: "100%",
        minHeight: 64,
        padding: 8,
        borderRadius: 12,
        border: "1px solid var(--border)",
        background: "var(--card)",
        cursor: "pointer",
        textAlign: "left",
        ...press.pressStyle,
      }}
    >
      <span
        style={{
          width: 56,
          height: 56,
          borderRadius: 8,
          flexShrink: 0,
          overflow: "hidden",
          background: "var(--midas-soft)",
          color: "var(--midas)",
          display: "grid",
          placeItems: "center",
        }}
      >
        {blob ? (
          <img src={blob} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <Spark size={18} />
        )}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          style={{
            display: "block",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.04em",
            color: "var(--midas)",
          }}
        >
          {card.label}
        </span>
        {card.title && (
          <span
            style={{
              display: "block",
              marginTop: 2,
              fontSize: 14,
              fontWeight: 600,
              color: "var(--ink)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {card.title}
          </span>
        )}
      </span>
      <Glyph d={GD.chev} size={16} style={{ color: "var(--faint)", flexShrink: 0 }} />
    </button>
  );
}

export default PageArtifactCard;
